/**
 * We use English comments only in this project.
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import { Libraries, useJsApiLoader } from '@react-google-maps/api'
import { ArrowLeft, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

// Import constants and utilities
import { 
  GOOGLE_MAPS_API_KEY, 
  mapLibraries as libraries, 
  defaultCenter as importedDefaultCenter,
  ringRoadBounds,
  serviceAreaCoordinates
} from '../../lib/map/constants'
import { 
  logDebug, 
  createModernAutocomplete,
  isLocationWithinServiceArea,
  findNearbyPlacesModern,
} from '../../lib/map/utils'

// Import custom hooks
import { 
  useDebounce, 
} from '../../lib/map/hooks'

// Import Zustand store
import { useMapStore, ensurePlacesApiLoaded, Location } from '../../lib/map/store'

// Import UI components
import { SearchBar } from './SearchBar'
import { AddressDisplay } from './AddressDisplay'
import { NearbyLocations } from './NearbyLocations'
import { MapPin } from './MapPin'
import { GoogleMapComponent } from './GoogleMapComponent'
import AddressForm from './AddressForm'
import { toast } from 'react-hot-toast'

// Import services
import addressService from '../../services/addressService'
import apiClient from '../../services/api'

// Import new utility functions
import { setupMap, showMapToast } from '../../lib/map/mapControls'

// Import the mapController
import { mapController } from '../../lib/map/mapController'

// Import lock manager
import { useLockManager } from '../../lib/map/lockManager'

// Import location utilities
import {
  StandardLocation,
  standardizeLocation,
  prepareForSaving,
  extractAddressComponents,
  formatFullAddress,
  extractLocationName as utilExtractLocationName,
  areLocationsNearlyIdentical,
  saveTemporaryLocation,
  getTemporaryLocation,
  clearTemporaryLocation
} from '../../lib/map/locationUtils'

// Local override for default center
const defaultCenter = {lat: 18.800341096685948, lng: 98.96821077796284};

interface MapLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectLocation: (locationData: {
    address: string;
    locationName: string;
    latitude: number;
    longitude: number;
  }) => void;
  initialCoords?: { lat: number; lng: number };
  editMode?: boolean;
  addressToEdit?: any;
  userPhone?: string;
  onShowMapModal?: () => void;
}

export default function MapLocationModal({
  isOpen,
  onClose,
  onSelectLocation,
  initialCoords,
  editMode = false,
  addressToEdit,
  userPhone,
  onShowMapModal
}: MapLocationModalProps) {
  // Initialize translation
  const { t } = useTranslation()
  
  // If not open, don't render anything
  if (!isOpen) return null;
  
  // Initialize Google Maps API loader
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries as Libraries,
  });
  
  // Access the Zustand store
  const { 
    markerPosition, 
    address, 
    locationName, 
    alternateAddresses, 
    isWithinServiceArea,
    searchQuery,
    isSearching,
    isLoading: storeIsLoading,
    map,
    currentStep,
    setMarkerPosition,
    setAddress,
    setLocationName,
    setAlternateAddresses,
    updatePosition,
    setSearchQuery,
    setIsSearching,
    setIsWithinServiceArea,
    setCurrentStep,
    setMap,
    getUserLocation,
    geocodePosition,
    searchAddress,
    findNearbyPlaces
  } = useMapStore();
  
  // Initialize currentStep
  useEffect(() => {
    // If in edit mode, set to address details step, otherwise set to location selection step
    setCurrentStep(editMode ? 2 : 1);
  }, [editMode, setCurrentStep]);
  
  // Language selection (automatically detects from browser, but allows manual override)
  const [language, setLanguage] = useState<'en' | 'th'>('en');
  
  // Refs
  const mapRef = useRef<google.maps.Map | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const idleListenerRef = useRef<google.maps.MapsEventListener | null>(null)
  const serviceAreaPolygonRef = useRef<google.maps.Polygon | null>(null)
  
  // Reference to last position for map movement tracking
  const lastPositionRef = useRef(markerPosition);
  
  // Update lastPositionRef when markerPosition changes
  useEffect(() => {
    lastPositionRef.current = markerPosition;
  }, [markerPosition]);
  
  // Check if location is within service area
  // NOTE: This function ensures consistent service area detection across the entire application
  // by always using the standardized isLocationWithinServiceArea utility function.
  // This prevents inconsistencies between visual polygon and detection logic.
  const checkIsWithinServiceArea = useCallback((position: {lat: number, lng: number}) => {
    // Always use the standardized utility function to ensure consistency
    return isLocationWithinServiceArea(position);
  }, []);
  
  // Create a throttle function for findNearbyPlaces to prevent too many calls
  const throttleTimeout = useRef<number | null>(null);
  const lastNearbySearchPosition = useRef<{ lat: number; lng: number } | null>(null);
  
  // Throttled function for nearby places search
  const throttledFindNearbyPlaces = useCallback((
    map: google.maps.Map, 
    position: { lat: number; lng: number },
    address?: string
  ) => {
    // Skip if we recently searched at nearly the same position
    if (lastNearbySearchPosition.current) {
      const isNearlyIdentical = 
        Math.abs(position.lat - lastNearbySearchPosition.current.lat) < 0.0008 && 
        Math.abs(position.lng - lastNearbySearchPosition.current.lng) < 0.0008;
      
      if (isNearlyIdentical) {
        console.log('[MapLocationModal] Skipping nearby search - position nearly identical to recent search');
        return;
      }
    }
    
    // Clear any pending throttle
    if (throttleTimeout.current !== null) {
      window.clearTimeout(throttleTimeout.current);
      throttleTimeout.current = null;
    }
    
    // Store position immediately to prevent duplicate calls during throttle period
    lastNearbySearchPosition.current = { ...position };
    
    // Set a new throttle with increased delay
    throttleTimeout.current = window.setTimeout(() => {
      console.log('[MapLocationModal] Executing throttled nearby places search');
      
      // Execute the search only if this is still the latest position
      findNearbyPlacesModern(map, position, address)
        .then(places => {
          if (places && places.length > 0) {
            console.log('[MapLocationModal] Found nearby places:', places.length);
            setAlternateAddresses(places);
          }
        })
        .catch(error => {
          console.error('[MapLocationModal] Error finding nearby places:', error);
        })
        .finally(() => {
          throttleTimeout.current = null;
        });
    }, 2000); // Increased to 2 seconds throttle to reduce API calls
  }, [setAlternateAddresses]);
  
  // Update handleMapMoveEnd to use the new check function
  const handleMapMoveEnd = useCallback(() => {
    if (!map) {
      logDebug('handleMapMoveEnd called but map is not loaded yet')
      return
    }
    
    logDebug('handleMapMoveEnd executing')
    
    const center = map.getCenter()
    if (!center) {
      logDebug('handleMapMoveEnd: Unable to get map center')
      return
    }
    
    const newPosition = {
      lat: center.lat(),
      lng: center.lng()
    }
    
    // Check if the new position is nearly identical to the current marker position
    // This prevents redundant updates when positions barely change
    const isNearlyIdentical = 
      Math.abs(newPosition.lat - lastPositionRef.current.lat) < 0.0001 && 
      Math.abs(newPosition.lng - lastPositionRef.current.lng) < 0.0001;
    
    if (isNearlyIdentical) {
      logDebug('Map position nearly identical to last position, skipping update');
      return;
    }
    
    logDebug('Map idle at position', newPosition)
    
    // Check if this was a manual adjustment after a search
    if (searchQuery.trim() && 
        (Math.abs(newPosition.lat - lastPositionRef.current.lat) > 0.0001 || 
         Math.abs(newPosition.lng - lastPositionRef.current.lng) > 0.0001)) {
      logDebug('Manual map adjustment detected after search, clearing search query')
      setSearchQuery('')
    }
    
    // Use mapController to handle position update
    // This replaces all the duplicate code for updating position, geocoding, etc.
    mapController.updateMapPosition(map, newPosition, {
      geocode: true,
      findNearby: false, // Don't use the built-in nearby places search
      zoom: null,
      removeListeners: false, // Don't remove the idle listener that triggered this
      restoreListeners: false, // Don't re-add listeners as we're already in an event handler
      updateMarker: true,
      delay: 0 // Don't delay as we're handling within the event
    });
    
    // Use our throttled function for nearby places search instead
    throttledFindNearbyPlaces(map, newPosition, useMapStore.getState().address);
    
    // Update the last position reference for later comparison
    lastPositionRef.current = newPosition
  }, [
    map, 
    searchQuery, 
    setSearchQuery,
    throttledFindNearbyPlaces
  ]);
  
  // Function to remove idle listener safely
  const removeIdleListener = useCallback(() => {
    mapController.removeMapListeners();
  }, []);
  
  // Function to add idle listener with safeguards
  const addIdleListener = useCallback(() => {
    if (!mapRef.current) return;
    
    // Use the mapController to add map listeners
    mapController.addMapListeners(mapRef.current);
  }, []);
  
  // Add idle listener when MapLocationModal is first rendered
  useEffect(() => {
    if (map) {
      addIdleListener();
      
      // Clean up listener when component unmounts
      return () => {
        removeIdleListener();
      };
    }
  }, [map, addIdleListener, removeIdleListener]);
  
  // Check if Google Maps API is already preloaded
  const [mapsPreloaded, setMapsPreloaded] = useState(
    window.google && window.google.maps && window.google.maps.places ? true : false
  );
  
  // Use manual loading state
  const [mapIsLoading, setMapIsLoading] = useState(!mapsPreloaded);
  
  // Debounce search query to avoid excessive API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  
  // Detect browser language and set Google Maps language accordingly
  const getPreferredLanguage = useCallback(() => {
    // Return fixed English language
    return 'en';
  }, []);
  
  // Add state for autocomplete that wasn't moved to Zustand
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  
  // Add state to track if position was just updated by autocomplete
  const [autocompleteUpdated, setAutocompleteUpdated] = useState(false);
  
  // Move handleUseCurrentLocation before it's used in toggleLanguage
  const handleUseCurrentLocation = () => {
    // Use the centralized controller instead of duplicating logic
    if (map) {
      mapController.useCurrentLocation(map);
    } else {
      console.error('Cannot use current location: map is not initialized');
      toast.error('Map is not ready. Please try again in a moment.');
    }
  };
  
  // Debug effect to force map to redraw when marker position changes
  useEffect(() => {
    console.log('[DEBUG] Main component detected marker position change:', markerPosition);
    
    // Check if we have a valid map instance
    if (!map) {
      console.log('[DEBUG] No map instance available, skipping position update');
      return;
    }
    
    const mapStoreState = useMapStore.getState();
    
    // Check if the map center is already very close to marker position to prevent redundant updates
    const currentCenter = map.getCenter();
    if (currentCenter) {
      const currentCenterPosition = {
        lat: currentCenter.lat(),
        lng: currentCenter.lng()
      };
      
      // Calculate distance between current center and marker position
      const isNearlyIdentical = 
        Math.abs(currentCenterPosition.lat - markerPosition.lat) < 0.0001 && 
        Math.abs(currentCenterPosition.lng - markerPosition.lng) < 0.0001;
      
      // Skip recenter if positions are nearly identical
      if (isNearlyIdentical) {
        console.log('[DEBUG] Skipping recenter - map center already matches marker position');
        return;
      }
    }
    
    // For address-locked states (like from search suggestions), we still want to update the map visual
    // but we should be more careful about when to do it
    if (mapStoreState.isAddressLocked) {
      console.log('[DEBUG] Address is locked, but checking if we should update map visual for search selection');
      
      // Allow map visual update if this is from a search selection (isFromSearch flag)
      if (mapStoreState.isFromSearch) {
        console.log('[DEBUG] Updating map visual for search selection despite address lock');
        
        // Remove idle listener temporarily to prevent conflicts
        removeIdleListener();
        
        // Update map position directly without triggering other effects
        map.setCenter(markerPosition);
        map.panTo(markerPosition);
        map.setZoom(18); // Ensure good zoom level for selected location
        
        // Force map redraw
        setTimeout(() => {
          google.maps.event.trigger(map, 'resize');
          
          // Re-add idle listener after update
          setTimeout(() => {
            addIdleListener();
          }, 500);
        }, 100);
        
        return;
      } else {
        console.log('[DEBUG] Address is locked and not from search, skipping map update');
        return;
      }
    }
    
    // Only proceed if programmatic update is not in progress
    if (!mapStoreState.isProgrammaticUpdate) {
      // Set a flag to indicate we're handling the marker position change
      const positionUpdateTimer = setTimeout(() => {
        console.log('[DEBUG] Forcing map recenter to match marker position with debounce');
        
        // Double check we're still not in a programmatic update before forcing recenter
        const currentState = useMapStore.getState();
        if (currentState.isAddressLocked && !currentState.isFromSearch) {
          console.log('[DEBUG] Address is locked, cancelling delayed position update');
          return;
        }
        
        if (!currentState.isProgrammaticUpdate) {
          // Set flag to prevent idle listener from triggering updates while we recenter
          useMapStore.setState({ isProgrammaticUpdate: true });
          
          // Move map to marker position
          map.panTo(markerPosition);
          
          // Reset programmatic update flag after a short delay
          setTimeout(() => {
            useMapStore.setState({ isProgrammaticUpdate: false });
          }, 500);
        } else {
          console.log('[DEBUG] Skipping debounced recenter due to programmatic update flag');
        }
      }, 300); // 300ms debounce to prevent rapid-fire updates
      
      // Clean up the timer if the component unmounts or markerPosition changes again
      return () => {
        clearTimeout(positionUpdateTimer);
      };
    } else {
      console.log('[DEBUG] Programmatic update in progress, skipping manual position update');
    }
  }, [markerPosition, map, removeIdleListener, addIdleListener]);
  
  // Effect to clear the isFromSearch flag after map has been updated
  useEffect(() => {
    const mapStoreState = useMapStore.getState();
    
    // If we're coming from search and the map exists, clear the flag after a delay
    if (mapStoreState.isFromSearch && map && markerPosition) {
      console.log('[DEBUG] Clearing isFromSearch flag after map update');
      
      const clearTimer = setTimeout(() => {
        useMapStore.setState({ 
          isFromSearch: false,
          // Also unlock the address after successful map update to allow normal map interactions
          isAddressLocked: false
        });
        console.log('[DEBUG] Cleared isFromSearch flag and unlocked address');
      }, 2000); // Wait 2 seconds for map to settle
      
      return () => clearTimeout(clearTimer);
    }
  }, [map, markerPosition]);
  
  // Handle search from SearchBar component to update the map position
  const handleSearchFromAutocomplete = useCallback((query: string) => {
    console.log('[MapLocationModal] Received search from autocomplete:', query);
    
    // Check if address is locked before proceeding
    const { isAddressLocked } = useMapStore.getState();
    if (isAddressLocked) {
      console.log('[MapLocationModal] Address is locked, skipping search update');
      // Still show map selection interface
      setCurrentStep(1);
      return;
    }
    
    // Ensure search switches to map selection interface
    setCurrentStep(1);
    
    // If the query is empty or very short, it might be from a direct user input rather than autocomplete selection
    // Only set the flag for likely autocomplete selections (longer text)
    if (query.length > 3) {
      setAutocompleteUpdated(true);
      
      // Reset the flag after a short delay to allow for manual searches again
      setTimeout(() => {
        setAutocompleteUpdated(false);
      }, 2000);
    }
    
    // Set flag to indicate this is a programmatic update
    useMapStore.setState({ isProgrammaticUpdate: true });
    
    // Set searching state
    setIsSearching(true);
    
    // This will trigger the useEffect with debouncedSearchQuery
    setSearchQuery(query);
  }, [setIsSearching, setSearchQuery, setCurrentStep]);
  
  // Initialize Places API with Autocomplete
  useEffect(() => {
    // Skip initialization - will be handled by SearchBar component
    if (isLoaded && searchInputRef.current) {
      console.log('[MapLocationModal] SearchBar will handle autocomplete initialization');
    } else if (loadError) {
      console.error('Google Maps API failed to load:', loadError);
    }
    
    return () => {
      if (autocomplete) {
        google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [isLoaded, loadError]);
  
  // Effect to perform geocoding when debounced search query changes
  useEffect(() => {
    // Skip if no query, if not loaded, or if we're not searching
    if (!debouncedSearchQuery.trim() || !isLoaded || !isSearching) {
      setIsSearching(false);
      // Also reset the programmatic update flag if not searching
      if (!isSearching) {
        useMapStore.setState({ isProgrammaticUpdate: false });
      }
      return;
    }
    
    // Use the lock manager to check if address is locked
    const lockManager = useLockManager.getState();
    if (lockManager.isLocked('address')) {
      console.log('[DEBUG] Address is locked, skipping search for:', debouncedSearchQuery);
      setIsSearching(false);
      return;
    }
    
    // Skip the search if we just updated from autocomplete selection
    // This prevents the double search problem
    if (autocompleteUpdated) {
      console.log('[DEBUG] Skipping search as position was just updated by autocomplete');
      setIsSearching(false);
      // Ensure to display location selection interface after automatic completion update
      setCurrentStep(1);
      return;
    }
    
    // Set loading state
    setAddress(t('map.searching'));
    console.log('[DEBUG] Starting search with query:', debouncedSearchQuery);
    
    // Lock the address and map while searching
    lockManager.lockMultiple(['address', 'map', 'search'], 'address search');
    
    // For backwards compatibility, still set the old flag
    if (!useMapStore.getState().isProgrammaticUpdate) {
      useMapStore.setState({ isProgrammaticUpdate: true });
      console.log('[DEBUG] Set isProgrammaticUpdate true for search');
    }
    
    // Use the store's modern search function with error handling
    useMapStore.getState().searchAddressModern(debouncedSearchQuery)
      .then(result => {
        console.log('[DEBUG] Search successful, position:', result.position);
        // Update marker position in store
        setMarkerPosition(result.position);
        updatePosition(result.position);
        
        // Update address and location data
        setAddress(result.address);
        setLocationName(result.locationName);
        
        // Check if the search result is within service area
        const isWithin = checkIsWithinServiceArea(result.position);
        setIsWithinServiceArea(isWithin);
        
        // Ensure to display location selection interface after search
        setCurrentStep(1);
        
        // If outside service area, show warning toast
        if (!isWithin && mapRef.current) {
          showMapToast(mapRef, t('map.locationOutsideToast'), 5000);
        }
        
        // Move map to new position
        if (map) {
          console.log('[DEBUG] Updating map position from search');
          // Remove the idle listener before panning
          removeIdleListener();
          
          // Move the map to the new position
          map.panTo(result.position);
          
          // Set appropriate zoom level
          map.setZoom(18);
          
          // Make sure map is centered at the marker position
          map.setCenter(result.position);
          
          // Force the map to redraw
          setTimeout(() => {
            google.maps.event.trigger(map, 'resize');
            
            // Find nearby places using the modern API
            findNearbyPlacesModern(map, result.position, result.address)
              .then(places => {
                if (places && places.length > 0) {
                  setAlternateAddresses(places);
                  console.log('[DEBUG] Updated nearby places for search result');
                }
              })
              .catch(error => {
                console.error('Error finding nearby places for search result:', error);
              })
              .finally(() => {
                // Re-add the idle listener after a delay
                setTimeout(() => {
                  console.log('[DEBUG] Adding idle listener back after search');
                  addIdleListener();
                  // Reset programmatic update flag
                  useMapStore.setState({ isProgrammaticUpdate: false });
                  console.log('[DEBUG] Reset isProgrammaticUpdate after search');
                }, 1000);
              });
          }, 300);
        } else {
          // Reset programmatic update flag even if map is not available
          setTimeout(() => {
            useMapStore.setState({ isProgrammaticUpdate: false });
            console.log('[DEBUG] No map - reset isProgrammaticUpdate after search');
          }, 500);
        }
      })
      .catch(error => {
        console.error('Search failed:', error);
        setAddress(t('map.noLocationFound'));
        setLocationName('');
        // Reset locks on error
        lockManager.unlockMultiple(['address', 'map', 'search'], 'search error');
        
        // For backwards compatibility
        useMapStore.setState({ isProgrammaticUpdate: false });
        console.log('[DEBUG] Error - reset isProgrammaticUpdate after search');
      })
      .finally(() => {
        setIsSearching(false);
        
        // Ensure locks are cleared after a delay to allow the map to settle
        setTimeout(() => {
          lockManager.unlockMultiple(['address', 'map', 'search'], 'search complete');
        }, 500);
      });
  }, [debouncedSearchQuery, isLoaded, isSearching, map, mapRef, updatePosition, setMarkerPosition, 
      addIdleListener, removeIdleListener, checkIsWithinServiceArea,
      setIsSearching, setAddress, setLocationName, setIsWithinServiceArea, setAlternateAddresses, t, autocompleteUpdated]);
  
  // Add state for form data
  const [formData, setFormData] = useState<{
    contactName?: string;
    contactPhone?: string;
    deliveryNote?: string;
  }>({});
  
  // Handle selecting a location from map
  const handleSelectLocation = async (location: any) => {
    console.log('[AddressManagementModal] Location selected:', location);
    
    try {
      // Standardize the location data
      const standardLocation = standardizeLocation(location);
      
      // Save to temporary storage for persistence
      saveTemporaryLocation(standardLocation);
      
      // Format the data for the API
      const formattedData = {
        address: standardLocation.address,
        locationName: standardLocation.locationName,
        latitude: standardLocation.position.lat,
        longitude: standardLocation.position.lng,
        // Add any additional data required by the parent component
        contactName: formData?.contactName,
        contactPhone: formData?.contactPhone || userPhone,
        deliveryNote: formData?.deliveryNote
      };
      
      // Call the callback with the prepared data
      onSelectLocation(formattedData);
      
      // Wait for a short time before closing the modal
      setTimeout(() => onClose(), 100);
    } catch (error) {
      console.error('[MapLocationModal] Error handling location selection:', error);
      toast.error('Something went wrong when saving your location. Please try again.');
    }
  };
  
  const handleMapInit = useCallback((map: google.maps.Map) => {
    logDebug('Map initialized');
    
    // Store reference to map
    mapRef.current = map;
    
    // Set programmatic update flag from the start to prevent race conditions
    useMapStore.setState({ isProgrammaticUpdate: true });
    
    // Update map in the global store
    setMap(map);
    
    // Try to draw service area polygon
    try {
      if (serviceAreaCoordinates && serviceAreaCoordinates.length > 0) {
        const polygon = new google.maps.Polygon({
          paths: serviceAreaCoordinates,
          strokeColor: '#4CAF50',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#4CAF50',
          fillOpacity: 0.1,
          map: map
        });
        
        serviceAreaPolygonRef.current = polygon;
      }
    } catch (error) {
      console.error('Error creating service area polygon:', error);
    }
    
    // First check if we have temporary location in localStorage (highest priority)
    const tempLocation = getTemporaryLocation();
    if (tempLocation) {
      console.log('[MapLocationModal] Found temporary location in localStorage:', tempLocation);
      
      // Clear it immediately after reading to prevent reuse
      clearTemporaryLocation();
    }
    
    // Check if we have addressDetails in the store from search
    const state = useMapStore.getState();
    console.log('[MapLocationModal] Current store state on map init:', {
      addressDetails: state.addressDetails,
      markerPosition: state.markerPosition,
      address: state.address,
      locationName: state.locationName
    });

    // CRITICAL FLAG: Track if we're using a specific location from search
    let isUsingSpecificLocation = false;
    
    // Determine initial position to show
    let initialPosition: {lat: number, lng: number};
    
    // Top priority: use temp location from localStorage if available
    if (tempLocation) {
      console.log('[MapLocationModal] Using temporary location from localStorage');
      
      initialPosition = tempLocation.position;
      isUsingSpecificLocation = true;
      
      // Also update the store with this data
      setAddress(tempLocation.address || '');
      setLocationName(tempLocation.locationName || '');
      
      // Also save to addressDetails for consistency
      useMapStore.setState({
        addressDetails: {
          formatted_address: tempLocation.address,
          name: tempLocation.locationName,
          latitude: tempLocation.position.lat,
          longitude: tempLocation.position.lng
        }
      });
    }
    // First priority: use addressDetails from search/autocomplete if available
    else if (state.addressDetails && state.addressDetails.latitude && state.addressDetails.longitude) {
      console.log('[MapLocationModal] Using addressDetails position for map initialization:', {
        lat: state.addressDetails.latitude,
        lng: state.addressDetails.longitude
      });
      
      initialPosition = {
        lat: state.addressDetails.latitude,
        lng: state.addressDetails.longitude
      };
      
      // Mark that we're using a specific location
      isUsingSpecificLocation = true;
    } 
    // Second priority: use address coordinates in edit mode
    else if (editMode && addressToEdit && addressToEdit.latitude && addressToEdit.longitude) {
      console.log('[MapLocationModal] Using addressToEdit position for map initialization:', { 
        lat: parseFloat(addressToEdit.latitude), 
        lng: parseFloat(addressToEdit.longitude) 
      });
      
      initialPosition = { 
        lat: parseFloat(addressToEdit.latitude), 
        lng: parseFloat(addressToEdit.longitude) 
      };
      
      // Mark that we're using a specific location
      isUsingSpecificLocation = true;
    } 
    // Third priority: use initialCoords if provided
    else if (initialCoords) {
      console.log('[MapLocationModal] Using initialCoords for map initialization:', initialCoords);
      initialPosition = initialCoords;
    }
    // Fourth priority: use current marker position if valid
    else if (state.markerPosition && state.markerPosition.lat !== 0) {
      console.log('[MapLocationModal] Using current markerPosition for map initialization:', state.markerPosition);
      initialPosition = state.markerPosition;
    }
    // Fallback: use default center
    else {
      console.log('[MapLocationModal] Using default center for map initialization');
      initialPosition = defaultCenter;
    }
    
    console.log('[MapLocationModal] Initializing map with position:', initialPosition);
    
    // Force map to the correct position with higher zoom
    map.setCenter(initialPosition);
    map.setZoom(18); // Even higher zoom level for better visibility
    
    // Update the marker position and map center in a single update to prevent multiple state changes
    setMarkerPosition(initialPosition);
    updatePosition(initialPosition);
    
    // Update the reference to the last position
    lastPositionRef.current = initialPosition;
    
    // For saved locations, apply additional force to ensure position is correct
    if (isUsingSpecificLocation) {
      // Force position updates with multiple methods and delays
      const enforcePosition = () => {
        console.log('[MapLocationModal] Enforcing exact position:', initialPosition);
        
        // Remove any listeners that might interfere
        removeIdleListener();
        
        // Multiple approaches to ensure position is set correctly
        map.setCenter(initialPosition);
        map.panTo(initialPosition);
        
        // Update marker directly
        setMarkerPosition(initialPosition);
        updatePosition(initialPosition);
        
        // Force map redraw
        google.maps.event.trigger(map, 'resize');
      };
      
      // Apply enforcement at multiple time points for reliability
      setTimeout(enforcePosition, 300);
      setTimeout(enforcePosition, 800);
      setTimeout(enforcePosition, 1500);
      
      // Add idle listener after a longer delay for specific locations
      setTimeout(() => {
        addIdleListener();
        // Reset programmatic update flag
        useMapStore.setState({ isProgrammaticUpdate: false });
      }, 2000);
    } else {
      // Set up map with boundaries and event listeners
      setupMap(map, ringRoadBounds, serviceAreaCoordinates);
      
      // Check if the initial position is within service area
      const isWithin = checkIsWithinServiceArea(initialPosition);
      setIsWithinServiceArea(isWithin);
      
      // Initialize by getting the address of the current position ONLY if we don't have specific location data
      if (!state.address || state.address === 'Loading address...') {
        setAddress(t('map.loadingAddress'));
        
        // Perform initial geocoding
        geocodePosition(initialPosition)
          .then(result => {
            setAddress(result.address);
            setLocationName(result.locationName);
            logDebug('Initial address loaded:', result.address);
            
            // If outside service area, show warning toast
            if (!isWithin) {
              showMapToast(mapRef, t('map.locationOutsideToast'), 5000);
            }
            
            // Find nearby places using the store's modern function
            findNearbyPlacesModern(map, initialPosition, result.address)
              .then(places => {
                if (places && places.length > 0) {
                  setAlternateAddresses(places);
                  logDebug('Initial nearby places loaded:', places);
                }
              })
              .catch(error => {
                logDebug('Error finding initial nearby places:', error);
              })
              .finally(() => {
                // Add idle listener after initialization is complete
                setTimeout(() => {
                  addIdleListener();
                  // Reset programmatic update flag
                  useMapStore.setState({ isProgrammaticUpdate: false });
                }, 1000);
              });
          })
          .catch(error => {
            setAddress(t('map.addressNotFound'));
            setLocationName(t('map.unknownLocation'));
            logDebug('Failed to load initial address:', error);
            
            // Add idle listener and reset flag even on error
            setTimeout(() => {
              addIdleListener();
              useMapStore.setState({ isProgrammaticUpdate: false });
            }, 1000);
          });
      } else {
        // If we already have an address, just add the idle listener and reset flag
        setTimeout(() => {
          addIdleListener();
          useMapStore.setState({ isProgrammaticUpdate: false });
        }, 1000);
      }
    }
    
    // Mark map as properly set for debugging
    logDebug('[MapLocationModal] Store map properly set:', !!useMapStore.getState().map);
  }, [addIdleListener, checkIsWithinServiceArea, setMap, setMarkerPosition, setAddress, setLocationName, 
      setIsWithinServiceArea, t, geocodePosition, findNearbyPlacesModern, initialCoords, editMode, 
      addressToEdit, updatePosition, setAlternateAddresses, removeIdleListener]);
  
  // Additional effect to handle switching to edit mode and initializing location data
  useEffect(() => {
    if (isLoaded && editMode && addressToEdit && map) {
      // Only process if we have map and valid address data
      if (addressToEdit.latitude && addressToEdit.longitude) {
        const addressCoords = {
          lat: parseFloat(addressToEdit.latitude),
          lng: parseFloat(addressToEdit.longitude)
        };
        
        logDebug('Edit mode: Setting address coordinates', addressCoords);
        
        // Set flag to prevent idle listener from overriding the intended position change
        useMapStore.setState({ isProgrammaticUpdate: true });
        
        // Update marker position in store
        setMarkerPosition(addressCoords);
        updatePosition(addressCoords);
        
        // Check if location is within service area
        const isWithin = checkIsWithinServiceArea(addressCoords);
        setIsWithinServiceArea(isWithin);
        
        // Move map to address coordinates with appropriate zoom
        map.panTo(addressCoords);
        map.setZoom(18);
        map.setCenter(addressCoords);
        
        // Force map to redraw
        setTimeout(() => {
          google.maps.event.trigger(map, 'resize');
          
          // Reset programmatic update flag after a delay
          setTimeout(() => {
            useMapStore.setState({ isProgrammaticUpdate: false });
          }, 1000);
        }, 300);
      }
    }
  }, [isLoaded, editMode, addressToEdit, map, updatePosition, setMarkerPosition, setIsWithinServiceArea, checkIsWithinServiceArea]);
  
  // Initialize map with user location, initial coordinates, or default center
  useEffect(() => {
    if (isLoaded && !loadError) {
      // Geocode the initial position after a short delay to ensure map is ready
      const timer = setTimeout(() => {
        if (!address && map) {
          // Trigger handleMapMoveEnd to get the address
          handleMapMoveEnd()
        }
      }, 1000)
      
      return () => clearTimeout(timer)
    }
  }, [isLoaded, loadError, handleMapMoveEnd, address, map])
  
  // Add an interval to check for stale locks
  useEffect(() => {
    // Start a periodic check for stale locks
    const intervalId = setInterval(() => {
      useLockManager.getState().checkAndReleaseStaleAllLocks();
    }, 5000); // Check every 5 seconds
    
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Add cleanup for the throttle timeout
  useEffect(() => {
    return () => {
      // Clean up map resources when component unmounts
      if (mapRef.current) {
        // Clear map event listeners
        google.maps.event.clearInstanceListeners(mapRef.current);
      }
      
      // Clean up autocomplete
      if (autocomplete) {
        google.maps.event.clearInstanceListeners(autocomplete);
      }
      
      // Clear throttle timeout
      if (throttleTimeout.current !== null) {
        window.clearTimeout(throttleTimeout.current);
      }
      
      // Remove idle listener
      removeIdleListener();
      
      // Reset all locks when unmounting to prevent lingering locks
      useLockManager.getState().resetAllLocks();
      console.log('[MapLocationModal] Component unmounting - all locks reset');
    };
  }, [removeIdleListener, autocomplete]);
  
  const navigate = useNavigate()
  
  const handleUseThisLocation = () => {
    navigate('/save-address', {
      state: {
        fromMapLocation: true,
        locationName: locationName,
        latitude: markerPosition.lat,
        longitude: markerPosition.lng,
        details: address
      }
    });
  }
  
  // Handle map click - update with service area check
  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng && map) {
      const newPos = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng()
      }
      
      logDebug('Map clicked at position', newPos)
      
      // Clear search query when clicking on map directly
      if (searchQuery.trim()) {
        logDebug('Clearing search query after map click')
        setSearchQuery('')
      }
      
      // Check if we currently have any active locks
      if (useLockManager.getState().isLocked('map') || useLockManager.getState().isLocked('position')) {
        console.log('[MapLocationModal] Skipping map click - map is currently locked');
        return;
      }
      
      // Use the lighter version for map clicks to avoid lock issues
      mapController.updateMapPositionLite(map, newPos);
    }
  }, [map, searchQuery, setSearchQuery]);
  
  const handleProceedToDetails = () => {
    // First check if location is within service area
    if (!isWithinServiceArea) {
      // Display clear feedback that the location is outside the service area
      toast.error(t('map.outsideDeliveryZoneError'));
      
      // Also show a toast on the map for increased visibility
      showMapToast(mapRef, t('map.selectInAreaToast'), 5000);
      
      return; // Prevent proceeding
    }
    
    // Only proceed if we have a valid location and it's within service area
    if (address && isWithinServiceArea) {
      setCurrentStep(2);
    }
  };
  
  // Go back to location selection
  const handleBackToMap = () => {
    setCurrentStep(1);
  }
  
  // 2. Edit address delay issue - Optimize map loading and address initialization

  // Add explicit drag listeners for debugging and to unlock address on manual drag
  if (map) {
    map.addListener('dragstart', () => {
      logDebug('Map drag started');
      
      // Clear any existing toast notifications when map drag starts
      try {
        const existingToast = document.querySelector('.map-ready-toast');
        if (existingToast && existingToast.parentNode) {
          existingToast.parentNode.removeChild(existingToast);
        }
      } catch (error) {
        console.error('[MapLocationModal] Error removing existing toast:', error);
      }
    });
    
    map.addListener('dragend', () => {
      logDebug('Map drag ended');
      
      // Get new center position
      const currentCenter = map.getCenter();
      if (currentCenter) {
        const newPosition = {
          lat: currentCenter.lat(),
          lng: currentCenter.lng()
        };
        
        // Unlock the address when user manually drags the map
        // This is a key fix - we need to unlock the address after manual map movement
        console.log('[MapLocationModal] User manually moved map, unlocking address');
        useMapStore.setState({ 
          isAddressLocked: false,
          isProgrammaticUpdate: false 
        });
        
        // Check if the new position is within service area
        const isWithin = checkIsWithinServiceArea(newPosition);
        setIsWithinServiceArea(isWithin);
        
        // Show warning if outside service area
        if (!isWithin && mapRef.current) {
          console.log('[MapLocationModal] New position is outside service area, showing warning');
          showMapToast(mapRef, t('map.locationOutsideToast'), 5000);
        }
        
        // Find nearby places for the new position
        if (map) {
          // Use a short timeout to ensure position updates first
          setTimeout(() => {
            findNearbyPlacesModern(map, newPosition, useMapStore.getState().address)
              .then(places => {
                if (places && places.length > 0) {
                  console.log('[MapLocationModal] Found nearby places after drag:', places.length);
                  setAlternateAddresses(places);
                }
              })
              .catch(error => {
                console.error('[MapLocationModal] Error finding nearby places after drag:', error);
              });
          }, 300);
        }
        
        // Log the new position for debugging
        logDebug('New map center after drag:', newPosition);
      }
    });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center overflow-hidden">
      <div className="bg-white flex flex-col w-full h-full md:w-[700px] md:h-[90vh] md:max-h-[900px] md:rounded-xl md:shadow-xl overflow-hidden">
        {/* Header - Reduced height and padding on mobile */}
        <div className="bg-white p-1.5 md:p-4 flex items-center justify-between border-b border-gray-200 h-12 md:h-auto">
          <div className="flex items-center">
            {currentStep === 2 && (
              <button
                onClick={handleBackToMap}
                className="mr-1.5 md:mr-4"
              >
                <ArrowLeft size={18} className="md:w-6 md:h-6" />
              </button>
            )}
            <h1 className="text-sm md:text-xl font-bold">
              {currentStep === 1 ? t('map.selectLocation') : (editMode ? t('map.editAddress') : t('map.saveAddress'))}
            </h1>
          </div>
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="p-1 md:p-2 hover:bg-gray-100 rounded-full"
            >
              <X size={18} className="md:w-6 md:h-6 text-gray-600" />
            </button>
          </div>
        </div>

        {currentStep === 1 ? (
          // STEP 1: Location Selection
          <>
            {/* Map container - Changed from flex-1 to fixed height */}
            <div 
              className="h-[45vh] bg-gray-200 flex items-center justify-center relative" 
              style={{ 
                pointerEvents: 'auto',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {loadError && <p className="text-red-500">{t('map.loadingMapError')}</p>}
              
              {/* Add a better loading indicator */}
              {!isLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 z-10">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 mb-4"></div>
                  <p className="text-gray-700 font-medium">{t('map.loadingMap')}</p>
                  <p className="text-gray-500 text-sm mt-2">{t('map.loadingMapHint')}</p>
                </div>
              )}
              
              {/* Google Map */}
              <GoogleMapComponent
                isLoaded={isLoaded}
                onLoad={handleMapInit}
                onClick={handleMapClick}
                onDragEnd={handleMapMoveEnd}
              />
              
              {/* Map Pin */}
              {isLoaded && <MapPin />}
              
            </div>
            
            {/* Bottom controls - Changed to have scrollable content with fixed button at bottom */}
            <div className="relative bg-white shadow-lg z-30 flex-1 overflow-y-auto pb-[74px] md:pb-[88px]">
              {/* Search bar with improved visibility */}
              <div className="px-4 pt-4 pb-3 md:p-5 border-b min-h-[70px] flex items-center">
                <div className="w-full" style={{ minHeight: '44px' }}>
                  {/* SearchBar component */}
                  <SearchBar
                    inputRef={searchInputRef}
                    className="bg-white py-3 text-base" 
                    onSearch={handleSearchFromAutocomplete}
                    onShowMapModal={onShowMapModal}
                  />
                </div>
              </div>
              
              {/* AddressDisplay component */}
              <AddressDisplay />
              
              {/* Service area warning */}
              {!isWithinServiceArea && address && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2 md:p-3 mx-4 mb-1 md:mb-2">
                  <p className="text-red-600 text-xs md:text-sm font-medium">
                    {t('map.outsideServiceArea')}
                  </p>
                </div>
              )}
              
              {/* NearbyLocations component */}
              <NearbyLocations />
            </div>

            {/* Fixed button container at bottom */}
            <div className="fixed bottom-0 left-0 right-0 px-4 pt-1 pb-0 md:px-4 md:py-4 md:pb-4 w-full bg-white border-t border-gray-200 z-40">
              <button 
                onClick={handleProceedToDetails}
                data-action="proceed-to-details"
                className={`w-full h-[50px] md:h-[65px] rounded-[16px] font-medium text-black transition-all duration-300 ${
                  address && isWithinServiceArea 
                    ? 'bg-[#F9D348] hover:bg-[#E7C337]' 
                    : 'bg-gray-300 cursor-not-allowed opacity-70'
                }`}
                disabled={!address || !isWithinServiceArea}
              >
                {isWithinServiceArea 
                  ? t('map.continueWithLocation')
                  : t('map.locationOutsideZone')}
              </button>
              {/* Hint text visible on all screen sizes */}
              <p className="text-center text-gray-500 text-xs md:text-sm mt-2 mb-1">
                {isWithinServiceArea 
                  ? t('map.nextStepHint')
                  : t('map.selectInAreaHint')}
              </p>
            </div>
            
            {/* Use current location button - Fixed at top center */}
            <div className="fixed top-[60px] md:top-[80px] left-1/2 transform -translate-x-1/2 z-40" style={{ pointerEvents: 'auto' }}>
              <button 
                onClick={handleUseCurrentLocation}
                className="flex items-center gap-2 bg-white py-1 md:py-2 px-3 md:px-4 rounded-full shadow-md border border-gray-300"
                disabled={mapIsLoading}
              >
                <span className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="12" r="2" fill="currentColor"/>
                  </svg>
                </span>
                <span className="text-gray-800 text-xs md:text-sm font-medium">
                  {mapIsLoading ? t('map.gettingLocation') : t('map.currentLocation')}
                </span>
              </button>
            </div>
          </>
        ) : (
          // STEP 2: Address Form - Now using the AddressForm component
          <AddressForm
            markerPosition={markerPosition}
            editMode={editMode}
            addressToEdit={addressToEdit}
            userPhone={userPhone}
            onBack={handleBackToMap}
            onSave={(savedAddress) => {
              onSelectLocation(savedAddress);
              // Wait for a short time before closing the modal
              setTimeout(() => onClose(), 100);
            }}
            initialValues={{
              addressName: locationName,
              addressDetails: address,
              contactName: '',
              contactPhone: userPhone || '',
              deliveryNote: '',
              images: []
            }}
          />
        )}
      </div>
    </div>
  )
} 